import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Search, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, X } from 'lucide-react';
import { toast } from 'sonner';
import { useCartWishlist } from '@/context/CartWishlistContext';
import axios from 'axios';

// Import all the new components
import {
  recentlyViewedUtils,
  filterPreferencesUtils,
  NoImagePlaceholder,
  SkeletonGrid,
  FilterChips,
  QuickViewModal,
  EnhancedProductCard,
  RecentlyViewedSection
} from './ShopComponents';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PRODUCTS_PER_PAGE = 12;

// Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const getPageNumbers = () => {
    const pages = [];
    const showEllipsis = totalPages > 7;

    if (!showEllipsis) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const pages = getPageNumbers();

  return (
    <div className="flex items-center justify-center gap-2 mt-12">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`p-2 rounded-lg transition ${
          currentPage === 1
            ? 'bg-white/5 text-gray-600 cursor-not-allowed'
            : 'bg-white/10 hover:bg-white/20 text-white'
        }`}
      >
        <ChevronLeft size={20} />
      </button>

      {pages.map((page, index) => (
        page === '...' ? (
          <span key={`ellipsis-${index}`} className="px-2 text-gray-400">...</span>
        ) : (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`px-4 py-2 rounded-lg transition ${
              currentPage === page
                ? 'bg-red-600 text-white font-bold'
                : 'bg-white/10 hover:bg-white/20 text-white'
            }`}
          >
            {page}
          </button>
        )
      ))}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`p-2 rounded-lg transition ${
          currentPage === totalPages
            ? 'bg-white/5 text-gray-600 cursor-not-allowed'
            : 'bg-white/10 hover:bg-white/20 text-white'
        }`}
      >
        <ChevronRight size={20} />
      </button>
    </div>
  );
};

// Filter Section Component
const FilterSection = ({ title, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-white/10 pb-4 mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full mb-3 text-left"
      >
        <h3 className="font-semibold text-sm uppercase tracking-wide">{title}</h3>
        {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {isOpen && <div className="space-y-2">{children}</div>}
    </div>
  );
};

const Shop = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Load saved preferences
  const savedPreferences = filterPreferencesUtils.get();
  
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'All');
  const [selectedType, setSelectedType] = useState(searchParams.get('type') || 'All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || savedPreferences.sortBy || 'featured');
  const [priceRange, setPriceRange] = useState([0, 50000]);
  const [wishlistItems, setWishlistItems] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  
  // New state for Quick View
  const [quickViewProductId, setQuickViewProductId] = useState(null);
  
  const { user, updateWishlistCount, updateCartCount } = useCartWishlist();

  // Read URL params on mount
  useEffect(() => {
    const category = searchParams.get('category');
    const type = searchParams.get('type');
    const sort = searchParams.get('sort');
    const page = searchParams.get('page');

    if (category) {
      setSelectedCategory(category);
    }
    if (type) {
      setSelectedType(type);
    }
    if (sort) {
      setSortBy(sort);
    }
    if (page) {
      setCurrentPage(parseInt(page));
    }
  }, [searchParams]);

  useEffect(() => {
    fetchCategories();
    fetchProducts();
    if (user) {
      fetchWishlist();
    }
  }, [selectedCategory, selectedType, user]);

  useEffect(() => {
    setCurrentPage(1);
    setSearchParams({ 
      ...(selectedCategory !== 'All' && { category: selectedCategory }),
      ...(selectedType !== 'All' && { type: selectedType }),
      ...(sortBy !== 'featured' && { sort: sortBy }),
      page: '1'
    });
  }, [selectedCategory, selectedType, searchQuery, sortBy, priceRange, setSearchParams]);

  useEffect(() => {
    setSearchParams({ 
      ...(selectedCategory !== 'All' && { category: selectedCategory }),
      ...(selectedType !== 'All' && { type: selectedType }),
      ...(sortBy !== 'featured' && { sort: sortBy }),
      page: currentPage.toString()
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage, selectedCategory, selectedType, sortBy, setSearchParams]);

  // Save sort preference when it changes
  useEffect(() => {
    filterPreferencesUtils.save({ sortBy });
  }, [sortBy]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories?is_active=true`);
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Failed to load categories');
    }
  };

  const fetchWishlist = async () => {
    try {
      const response = await axios.get(`${API}/wishlist`, { withCredentials: true });
      const productIds = new Set(response.data.products.map(p => p.id));
      setWishlistItems(productIds);
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let url = `${API}/products`;
      const params = new URLSearchParams();
      if (selectedCategory !== 'All') params.append('category', selectedCategory);
      if (selectedType !== 'All') params.append('type', selectedType);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await axios.get(url);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPrice = product.price >= priceRange[0] && product.price <= priceRange[1];
    return matchesSearch && matchesPrice;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-low-high':
        return a.price - b.price;
      case 'price-high-low':
        return b.price - a.price;
      case 'name-a-z':
        return a.name.localeCompare(b.name);
      case 'name-z-a':
        return b.name.localeCompare(a.name);
      case 'newest':
        return new Date(b.created_at) - new Date(a.created_at);
      case 'rating':
        return (b.rating || 0) - (a.rating || 0);
      case 'popularity':
        return (b.review_count || 0) - (a.review_count || 0);
      case 'featured':
      default:
        if (a.featured === b.featured) {
          return new Date(b.created_at) - new Date(a.created_at);
        }
        return b.featured ? 1 : -1;
    }
  });

  const totalPages = Math.ceil(sortedProducts.length / PRODUCTS_PER_PAGE);
  const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
  const endIndex = startIndex + PRODUCTS_PER_PAGE;
  const currentProducts = sortedProducts.slice(startIndex, endIndex);

  const handleClearFilters = () => {
    setSelectedCategory('All');
    setSelectedType('All');
    setSearchQuery('');
    setSortBy('featured');
    setPriceRange([0, 50000]);
    setCurrentPage(1);
    toast.success('All filters cleared');
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleWishlistToggle = async (productId, e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please login to manage wishlist');
      const redirectUrl = `${window.location.origin}/auth-callback`;
      window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
      return;
    }

    const isInWishlist = wishlistItems.has(productId);
    const newWishlistItems = new Set(wishlistItems);
    
    if (isInWishlist) {
      newWishlistItems.delete(productId);
    } else {
      newWishlistItems.add(productId);
    }
    setWishlistItems(newWishlistItems);

    try {
      if (isInWishlist) {
        await axios.delete(`${API}/wishlist/remove/${productId}`, { withCredentials: true });
        updateWishlistCount(-1);
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${API}/wishlist/add?product_id=${productId}`, {}, { withCredentials: true });
        updateWishlistCount(1);
        toast.success('Added to wishlist');
      }
    } catch (error) {
      setWishlistItems(wishlistItems);
      toast.error(error.response?.data?.detail || 'Failed to update wishlist');
    }
  };

  // Quick View Handler
  const handleQuickView = (productId) => {
    setQuickViewProductId(productId);
  };

  // Add to Cart Handler (for both Quick View and Product Card)
  const handleAddToCart = async (product) => {
    if (!user) {
      toast.error('Please login to add items to cart');
      const redirectUrl = `${window.location.origin}/auth-callback`;
      window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
      return;
    }

    if (product.stock === 0) {
      toast.error('This product is out of stock');
      return;
    }

    try {
      await axios.post(
        `${API}/cart?product_id=${product.id}&quantity=1`,
        {},
        { withCredentials: true }
      );
      updateCartCount(1);
      toast.success(`Added ${product.name} to cart!`);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Please login to add items to cart');
        const redirectUrl = `${window.location.origin}/auth-callback`;
        window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login`;
      } else {
        toast.error('Failed to add to cart');
      }
    }
  };

  const activeFiltersCount = 
    (selectedCategory !== 'All' ? 1 : 0) +
    (selectedType !== 'All' ? 1 : 0) +
    (priceRange[0] !== 0 || priceRange[1] !== 50000 ? 1 : 0);

  // Sidebar Filters Component
  const SidebarFilters = () => (
    <div className="space-y-4">
      <FilterSection title="Categories">
        <div className="space-y-2">
          <label className="flex items-center space-x-2 cursor-pointer hover:text-red-500 transition">
            <input
              type="radio"
              name="category"
              checked={selectedCategory === 'All'}
              onChange={() => setSelectedCategory('All')}
              className="w-4 h-4 text-red-600 border-gray-600 focus:ring-red-500"
            />
            <span className="text-sm">All Categories</span>
          </label>
          {categories.map((category) => (
            <label key={category.id} className="flex items-center space-x-2 cursor-pointer hover:text-red-500 transition">
              <input
                type="radio"
                name="category"
                checked={selectedCategory === category.code}
                onChange={() => setSelectedCategory(category.code)}
                className="w-4 h-4 text-red-600 border-gray-600 focus:ring-red-500"
              />
              <span className="text-sm">{category.name} ({category.product_count})</span>
            </label>
          ))}
        </div>
      </FilterSection>

      <FilterSection title="Type">
        <div className="space-y-2">
          {['All', 'Action Figure', 'Figurine', 'Keychain', 'Bobblehead', 'Pendant', 'Cosplay'].map((type) => (
            <label key={type} className="flex items-center space-x-2 cursor-pointer hover:text-red-500 transition">
              <input
                type="radio"
                name="type"
                checked={selectedType === type}
                onChange={() => setSelectedType(type)}
                className="w-4 h-4 text-red-600 border-gray-600 focus:ring-red-500"
              />
              <span className="text-sm">{type === 'All' ? 'All Types' : type}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      <FilterSection title="Price Range">
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span>₹{priceRange[0].toLocaleString()}</span>
            <span>₹{priceRange[1].toLocaleString()}</span>
          </div>
          <input
            type="range"
            min="0"
            max="50000"
            step="500"
            value={priceRange[1]}
            onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
            className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-red-600"
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              type="number"
              value={priceRange[0]}
              onChange={(e) => setPriceRange([parseInt(e.target.value) || 0, priceRange[1]])}
              className="px-3 py-2 bg-white/5 border border-white/10 rounded text-sm focus:outline-none focus:border-red-500"
              placeholder="Min"
            />
            <input
              type="number"
              value={priceRange[1]}
              onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value) || 50000])}
              className="px-3 py-2 bg-white/5 border border-white/10 rounded text-sm focus:outline-none focus:border-red-500"
              placeholder="Max"
            />
          </div>
        </div>
      </FilterSection>
    </div>
  );

  return (
    <div className="min-h-screen bg-black" data-testid="shop-page">
      <Navbar />
      
      {/* Quick View Modal */}
      {quickViewProductId && (
        <QuickViewModal
          productId={quickViewProductId}
          onClose={() => setQuickViewProductId(null)}
          onAddToCart={handleAddToCart}
        />
      )}
      
      <div className="pt-24 pb-20">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl sm:text-4xl font-bold mb-2" data-testid="shop-title">
              Explore <span className="accent-red">Collectibles</span>
            </h1>
            <p className="text-gray-400 text-sm" data-testid="shop-description">
              Discover authentic collectibles from your favorite universes
            </p>
          </div>

          {/* Search and Sort Bar */}
          <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="relative w-full sm:max-w-md">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search collectibles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
                data-testid="search-input"
              />
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto">
              <button
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                className="lg:hidden flex items-center gap-2 px-4 py-3 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 transition"
              >
                <span className="text-sm">Filters</span>
                {activeFiltersCount > 0 && (
                  <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">{activeFiltersCount}</span>
                )}
              </button>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="flex-1 sm:flex-initial bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-red-500"
                data-testid="sort-filter"
              >
                <option value="featured">Featured</option>
                <option value="newest">Newest First</option>
                <option value="price-low-high">Price: Low to High</option>
                <option value="price-high-low">Price: High to Low</option>
                <option value="name-a-z">Name: A to Z</option>
                <option value="name-z-a">Name: Z to A</option>
                <option value="rating">Highest Rated</option>
                <option value="popularity">Most Popular</option>
              </select>
            </div>
          </div>

          {/* Filter Chips */}
          <FilterChips
            selectedCategory={selectedCategory}
            selectedType={selectedType}
            priceRange={priceRange}
            sortBy={sortBy}
            onClearCategory={() => setSelectedCategory('All')}
            onClearType={() => setSelectedType('All')}
            onClearPrice={() => setPriceRange([0, 50000])}
            onClearSort={() => setSortBy('featured')}
            onClearAll={handleClearFilters}
          />

          {/* Main Content */}
          <div className="flex gap-6">
            {/* LEFT SIDEBAR - Desktop */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-24 bg-white/5 border border-white/10 rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-bold text-lg">Filters</h2>
                  {activeFiltersCount > 0 && (
                    <button
                      onClick={handleClearFilters}
                      className="text-xs text-red-500 hover:text-red-400 underline"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                <SidebarFilters />
              </div>
            </aside>

            {/* Mobile Filters Overlay */}
            {showMobileFilters && (
              <div className="fixed inset-0 bg-black/80 z-50 lg:hidden">
                <div className="absolute right-0 top-0 bottom-0 w-full sm:w-96 bg-gray-900 overflow-y-auto">
                  <div className="sticky top-0 bg-gray-900 border-b border-white/10 p-4 flex items-center justify-between">
                    <h2 className="font-bold text-lg">Filters</h2>
                    <button
                      onClick={() => setShowMobileFilters(false)}
                      className="p-2 hover:bg-white/10 rounded-lg transition"
                    >
                      <X size={20} />
                    </button>
                  </div>
                  <div className="p-6">
                    <SidebarFilters />
                    <div className="mt-6 space-y-3">
                      <button
                        onClick={() => setShowMobileFilters(false)}
                        className="w-full btn-primary py-3"
                      >
                        Show {sortedProducts.length} Products
                      </button>
                      {activeFiltersCount > 0 && (
                        <button
                          onClick={() => {
                            handleClearFilters();
                            setShowMobileFilters(false);
                          }}
                          className="w-full btn-secondary py-3"
                        >
                          Clear All Filters
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* RIGHT - Products Grid */}
            <div className="flex-1 min-w-0">
              {!loading && sortedProducts.length > 0 && (
                <div className="mb-4 text-sm text-gray-400">
                  Showing {startIndex + 1}-{Math.min(endIndex, sortedProducts.length)} of {sortedProducts.length} products
                </div>
              )}

              {loading ? (
                <SkeletonGrid count={12} />
              ) : currentProducts.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6" data-testid="products-grid">
                    {currentProducts.map(product => (
                      <EnhancedProductCard
                        key={product.id}
                        product={product}
                        isInWishlist={wishlistItems.has(product.id)}
                        onWishlistToggle={handleWishlistToggle}
                        onQuickView={handleQuickView}
                        onAddToCart={handleAddToCart}
                      />
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  )}
                </>
              ) : (
                <div className="text-center py-20" data-testid="no-products">
                  <p className="text-xl text-gray-400 mb-2">No products found</p>
                  <button
                    onClick={handleClearFilters}
                    className="text-red-500 hover:text-red-400 underline"
                  >
                    Clear all filters
                  </button>
                </div>
              )}

              {/* Recently Viewed Section */}
              <RecentlyViewedSection 
                onProductClick={(id) => recentlyViewedUtils.add(id)}
              />
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Shop;